import { useState } from 'react';
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { fmtMoney } from '../../services/lohnBuchungsbelegApi';
import { analyzeChecksum } from '../../services/lohnChecksum';
import type { ComputedVoucher } from '../../types/lohnBuchungsbeleg';

interface Props {
    voucher: ComputedVoucher;
    editable: boolean;
}

/**
 * Balance indicator + expandable "Differenz erklären" analysis. When the checksum isn't 0,
 * it (a) flags the leaks that keep amounts out of the beleg (unmatched Lohnarten, missing
 * cost centers), (b) warns about employer-contribution rows wrongly counted in the checksum,
 * and (c) lists the checksum-relevant rows making up the sum (largest first), so the user
 * can see exactly which line drives the difference.
 */
export function ChecksumPanel({ voucher, editable }: Props) {
    const [open, setOpen] = useState(false);
    const a = analyzeChecksum(voucher);
    const balanced = voucher.balanced;

    return (
        <div className={`mb-3 rounded-md border text-sm ${balanced ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                {balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                <span className="font-medium">Checksum: <span className="font-mono">{fmtMoney(voucher.checksum, false)}</span></span>
                <span className="text-xs">{balanced ? 'Beleg ist ausgeglichen.' : 'Beleg weist eine Differenz auf.'}</span>
                {!balanced && (
                    <button onClick={() => setOpen(o => !o)} className="ml-1 inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100">
                        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Differenz erklären
                    </button>
                )}
                {editable && <span className="ml-auto text-xs text-slate-500">Zelle anklicken zum Überschreiben (mit Kommentar)</span>}
            </div>

            {!balanced && open && (
                <div className="space-y-3 border-t border-amber-200 px-3 py-2.5">
                    {/* Cost centers whose gross rows are missing from the raw export */}
                    {a.grossDeficits.length > 0 && (
                        <div className="rounded border border-rose-200 bg-rose-50 px-2.5 py-2">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-800">
                                <AlertTriangle className="h-3.5 w-3.5" /> Wahrscheinliche Ursache: Kostenstelle ohne Bruttolohn
                            </div>
                            <p className="mt-1 text-[11px] text-rose-700">
                                Für diese Kostenstelle{a.grossDeficits.length > 1 ? 'n' : ''} enthalten die Rohdaten AN-Abzüge,
                                aber keinen bzw. zu wenig Bruttolohn — vermutlich fehlen Zeilen im hochgeladenen Export
                                (Datei prüfen / beim Ersteller reklamieren). Übergangsweise kann die fehlende Zelle im Beleg
                                per Klick überschrieben werden (mit Kommentar).
                            </p>
                            <table className="mt-1.5 min-w-full text-xs">
                                <thead className="text-[10px] uppercase text-rose-400">
                                    <tr>
                                        <th className="py-0.5 pr-3 text-left font-medium">Kostenstelle</th>
                                        <th className="py-0.5 pr-3 text-right font-medium">Brutto</th>
                                        <th className="py-0.5 pr-3 text-right font-medium">AN-Abzüge</th>
                                        <th className="py-0.5 text-right font-medium">Geschätzt fehlend</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {a.grossDeficits.map(d => {
                                        const label = voucher.costCenters.find(c => c.code === d.costCenter)?.label;
                                        return (
                                            <tr key={d.costCenter} className="text-rose-900">
                                                <td className="py-0.5 pr-3">
                                                    <span className="font-mono">{d.costCenter}</span>
                                                    {label && <span className="ml-1 text-rose-500">{label}</span>}
                                                </td>
                                                <td className="whitespace-nowrap py-0.5 pr-3 text-right font-mono">{fmtMoney(d.gross, false)}</td>
                                                <td className="whitespace-nowrap py-0.5 pr-3 text-right font-mono">{fmtMoney(d.deductions, false)}</td>
                                                <td className="whitespace-nowrap py-0.5 text-right font-mono font-semibold">
                                                    {d.estimatedMissing !== null ? `~${fmtMoney(d.estimatedMissing, false)}` : '—'}
                                                    {d.estimatedMissing !== null && d.calibrationAccount && (
                                                        <span className="ml-1 font-sans text-[10px] font-normal text-rose-400">(Basis {d.calibrationAccount})</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Suspect employer-contribution rows in the checksum */}
                    {a.suspects.length > 0 && (
                        <div className="rounded border border-rose-200 bg-rose-50 px-2.5 py-2">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-800">
                                <AlertTriangle className="h-3.5 w-3.5" /> Wahrscheinliche Ursache: AG-Beitrag in der Kontrollsumme
                            </div>
                            <p className="mt-1 text-[11px] text-rose-700">
                                Diese Arbeitgeber-Beitrags-Zeile{a.suspects.length > 1 ? 'n' : ''} (Konto 57xxx) {a.suspects.length > 1 ? 'zählen' : 'zählt'} in die
                                Kontrollsumme — andere AG-Beiträge normalerweise nicht. Setze «In Checksumme» im Bearbeiten-Dialog
                                (Erweitert) auf «nein», falls das nicht gewollt ist.
                            </p>
                            <table className="mt-1.5 min-w-full text-xs">
                                <tbody>
                                    {a.suspects.map((r, i) => (
                                        <tr key={i} className="text-rose-900">
                                            <td className="py-0.5 pr-3">{r.beschreibung} <span className="font-mono text-rose-500">({r.soll}/{r.haben})</span></td>
                                            <td className="whitespace-nowrap py-0.5 text-right font-mono font-semibold">{fmtMoney(r.rowTotal, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Leaks that keep amounts out of the beleg */}
                    {a.leaks.length > 0 && (
                        <div>
                            <p className="mb-1 text-xs font-medium text-amber-800">Beträge, die nicht im Beleg enthalten sind:</p>
                            <table className="min-w-full text-xs">
                                <tbody>
                                    {a.leaks.map(l => (
                                        <tr key={l.key} className="border-t border-amber-100 first:border-t-0">
                                            <td className="py-1 pr-3 align-top">
                                                <span className="font-medium text-amber-900">{l.label}</span>
                                                <div className="text-[11px] font-normal text-amber-600">{l.detail}</div>
                                            </td>
                                            <td className="whitespace-nowrap py-1 text-right align-top font-mono font-semibold text-amber-900">{fmtMoney(l.amount, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Line-level breakdown of what makes up the checksum */}
                    <div>
                        <p className="mb-1 text-xs font-medium text-amber-800">Grösste Posten in der Kontrollsumme (Σ = Checksum):</p>
                        <div className="max-h-72 overflow-y-auto rounded border border-amber-100">
                            <table className="min-w-full text-xs">
                                <thead className="sticky top-0 bg-amber-100/80 text-amber-700">
                                    <tr>
                                        <th className="px-2 py-1 text-left font-semibold">Beschreibung</th>
                                        <th className="px-2 py-1 text-right font-semibold">Soll</th>
                                        <th className="px-2 py-1 text-right font-semibold">Haben</th>
                                        <th className="px-2 py-1 text-right font-semibold">Betrag</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {a.rows.map((r, i) => (
                                        <tr key={i} className={`border-t border-amber-100 ${r.suspectAgBeitrag ? 'bg-rose-50 text-rose-900' : 'text-amber-900'}`}>
                                            <td className="px-2 py-1">
                                                {r.beschreibung}
                                                {r.suspectAgBeitrag && <span className="ml-1 rounded bg-rose-200 px-1 text-[10px] text-rose-700">AG-Beitrag?</span>}
                                            </td>
                                            <td className="px-2 py-1 text-right font-mono text-amber-500">{r.soll}</td>
                                            <td className="px-2 py-1 text-right font-mono text-amber-500">{r.haben}</td>
                                            <td className="px-2 py-1 text-right font-mono font-semibold">{fmtMoney(r.rowTotal, false)}</td>
                                        </tr>
                                    ))}
                                    <tr className="border-t-2 border-amber-300 bg-amber-50 font-semibold">
                                        <td className="px-2 py-1" colSpan={3}>Total (= Checksum)</td>
                                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(voucher.checksum, false)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
